import { Injectable }                   from '@angular/core';
import {
	AdminConsoleRoutesEnum,
	AdminConsoleUserDetailPageRoute
}                                       from '@app/modules/admin-console/routes/admin-console.routes';
import { TenantGroupEnum }              from '@app/modules/admin-tenant/models/tenant.model';
import { TenantRoutesEnum }             from '@app/modules/admin-tenant/routes/admin-tenant.routes';
import {
	TenantConsoleRoutesEnum,
	TenantConsoleStatisticsPageRoute
}                                       from '@app/modules/tenant-console/routes/tenant-console.routes';
import { RealmEnum }                    from '@app/modules/user/models/user.model';
import {
	UserDetailPageRoute,
	UserRoutesEnum
}                                       from '@app/modules/user/routes/user.routes';
import { RouteBasePath }                from '@core/interfaces/route.interface';
import { AuthorizationService }         from '@core/services/authorization/authorization.service';
import { RouterService }                from '@core/services/route.service';
import {
	CurrentUserStoreActions,
	CurrentUserStoreSelectors
}                                       from '@core/store/feature/current-user';
import { Store }                        from '@ngrx/store';
import {
	IManageTenantMetadata,
	MANAGE_TENANT_METADATA
}                                       from '@shared/content-sections/manage-tenant/manage-tenant.config';
import {
	IManageTenant,
	ManageTenantAdminConsoleUserDetailPageTenantIdActionToOverview,
	ManageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails,
	ManageTenantIdentifierEnum,
	ManageTenantTenantDetailPage,
	ManageTenantTenantListPage,
	ManageTenantType
}                                       from '@shared/content-sections/manage-tenant/manage-tenant.model';
import { ListOperationsIdentifierEnum } from '@shared/list-operations/model/list-operations.model';
import { ListOperationsService }        from '@shared/list-operations/services/list-operations.service';
import { Observable }                   from 'rxjs';


@Injectable( { providedIn: 'root' } )
export class ManageTenantService {
	private readonly manageTenantMetadata: IManageTenantMetadata = MANAGE_TENANT_METADATA;

	private readonly _managedTenant$: Observable<IManageTenant>;

	private _managedTenant: IManageTenant;

	get managedTenant$(): Observable<IManageTenant> {
		return this._managedTenant$;
	}

	get managedTenant(): IManageTenant {
		return this._managedTenant;
	}

	constructor(
		private readonly authorizationService: AuthorizationService,
		private readonly listOperationsService: ListOperationsService,
		private readonly routerService: RouterService,
		private readonly store$: Store
	) {
		this._managedTenant$ = this.store$.select( CurrentUserStoreSelectors.manageTenant );
		this._managedTenant$
		.subscribe( ( managedTenant: IManageTenant ) => {
			this._managedTenant = managedTenant;
		} );
	}

		private getManageTenantAdminConsoleUserDetailPageTenantIdActionToOverview(
		manageTenantAdminConsoleUserDetailPageTenantIdActionToOverview: ManageTenantAdminConsoleUserDetailPageTenantIdActionToOverview
	): IManageTenant {
		const { data } = manageTenantAdminConsoleUserDetailPageTenantIdActionToOverview;

		const backRoute: AdminConsoleUserDetailPageRoute = {
			basePath: RouteBasePath.AdminConsole,
			path: AdminConsoleRoutesEnum.UserDetailPage,
			params: { userId: data?.userId }
		};

		const toRoute: TenantConsoleStatisticsPageRoute = {
			basePath: RouteBasePath.TenantConsole,
			path: TenantConsoleRoutesEnum.STATISTICS_PAGE
		};

		return {
			...manageTenantAdminConsoleUserDetailPageTenantIdActionToOverview,
			navigate: {
				back: {
					route: { ...backRoute }
				},
				to: {
					route: { ...toRoute }
				}
			},
			routeInfoOptions: this.manageTenantMetadata?.routeInfoOptionsMaps[ manageTenantAdminConsoleUserDetailPageTenantIdActionToOverview?.id ]
		};
	}

	private getManageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails(
		manageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails: ManageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails
	): IManageTenant {
		const { data } = manageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails;

		const backRoute: AdminConsoleUserDetailPageRoute = {
			basePath: RouteBasePath.AdminConsole,
			path: AdminConsoleRoutesEnum.UserDetailPage,
			params: { userId: data?.userId }
		};

		const toRoute: UserDetailPageRoute = {
			basePath: RouteBasePath.Users,
			path: UserRoutesEnum.DetailPage,
			params: { userId: data?.userId }
		};

		return {
			...manageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails,
			navigate: {
				back: {
					route: { ...backRoute }
				},
				to: {
					route: { ...toRoute }
				}
			},
			routeInfoOptions: this.manageTenantMetadata?.routeInfoOptionsMaps[ manageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails?.id ]
		};
	}

	private getManageTenantTenantDetailPage(
		manageTenantTenantDetailPageData: ManageTenantTenantDetailPage
	): IManageTenant {
		const { tenantId } = manageTenantTenantDetailPageData;

		return {
			...manageTenantTenantDetailPageData,
			navigate: {
				back: {
					route: {
						basePath: RouteBasePath.Tenant,
						path: TenantRoutesEnum.DetailPage,
						params: { tenantId }
					}
				},
				to: {
					route: {
						basePath: RouteBasePath.TenantConsole,
						path: TenantConsoleRoutesEnum.STATISTICS_PAGE
					}
				}
			},

			routeInfoOptions: this.manageTenantMetadata?.routeInfoOptionsMaps[ manageTenantTenantDetailPageData?.id ]
		};
	}

	private getManageTenantTenantListPage(
		manageTenantTenantListPage: ManageTenantTenantListPage
	): IManageTenant {
		return {
			...manageTenantTenantListPage,
			navigate: {
				back: {
					route: {
						basePath: RouteBasePath.Tenant,
						path: TenantRoutesEnum.ListPage
					},
					listOperation: {
						id: ListOperationsIdentifierEnum.ACCOUNTS_LIST_PAGE,
						shouldRestore: true
					}
				},
				to: {
					route: {
						basePath: RouteBasePath.TenantConsole,
						path: TenantConsoleRoutesEnum.STATISTICS_PAGE
					}
				}
			},
			routeInfoOptions: this.manageTenantMetadata?.routeInfoOptionsMaps[ manageTenantTenantListPage?.id ]
		};
	}

	getRealmByTenantGroup( tenantGroup: TenantGroupEnum ): RealmEnum {
		switch ( tenantGroup ) {
			case TenantGroupEnum.DeutscheFiskal:
				return RealmEnum.DF;

			// Add another case for other Brands

			default:
				return null;
		}
	}


	manageTenantActive( manageTenantDataType: ManageTenantType ) {
		let manageTenant: IManageTenant;

		switch ( manageTenantDataType?.id ) {
			case ManageTenantIdentifierEnum.ADMIN_CONSOLE_USER_DETAIL_PAGE_TENANT_ID_ACTION_TO_OVERVIEW:
				if ( manageTenantDataType instanceof ManageTenantAdminConsoleUserDetailPageTenantIdActionToOverview ) {
					manageTenant = this.getManageTenantAdminConsoleUserDetailPageTenantIdActionToOverview( manageTenantDataType );
				}
				break;

			case ManageTenantIdentifierEnum.ADMIN_CONSOLE_USER_DETAIL_PAGE_USER_ID_ACTION_TO_USER_DETAILS:
				if ( manageTenantDataType instanceof ManageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails ) {
					manageTenant = this.getManageTenantAdminConsoleUserDetailPageUserIdActionToUserDetails( manageTenantDataType );
				}
				break;

			case ManageTenantIdentifierEnum.TENANT_DETAIL_PAGE:
				if ( manageTenantDataType instanceof ManageTenantTenantDetailPage ) {
					manageTenant = this.getManageTenantTenantDetailPage( manageTenantDataType );
				}
				break;

			case ManageTenantIdentifierEnum.TENANT_LIST_PAGE:
				if ( manageTenantDataType instanceof ManageTenantTenantListPage ) {
					manageTenant = this.getManageTenantTenantListPage( manageTenantDataType );
				}
				break;
		}

		if ( !manageTenant ) { return; }

		this.store$.dispatch( new CurrentUserStoreActions.ManageTenantActive( manageTenant ) );

		this.routerService.clearRoutes( '' );

		this.routerService.navigateToNextRoute(
			manageTenant?.navigate?.to?.route,
			'',
			false,
			false,
			false,
			true
		);
	}

	manageTenantInactive() {
		if ( this.managedTenant?.navigate?.back?.listOperation ) {
			this.listOperationsService.state.listOperationsSetRestoreFlag(
				this.managedTenant?.navigate?.back?.listOperation?.id,
				this.managedTenant?.navigate?.back?.listOperation?.shouldRestore,
				'caller'
			);
		}

		this.routerService.clearRoutes( '' );

		this.routerService.navigateToNextRoute(
			this.managedTenant?.navigate?.back?.route,
			'',
			true
		);

		this.store$.dispatch( new CurrentUserStoreActions.ManageTenantInactive() );
	}
}
